/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Right-side action cluster for the unauthenticated GlobalHeader:
 * language selector menu and the sign-in CTA.
 */

import { Menu, UnstyledButton } from'@/shared/ui';
import { useTranslation } from'react-i18next';
import { cn } from'@/shared/ui/utils/tailwind';
import { getLanguageLabel, getLanguageFullName, normalizeLanguage } from'./GlobalHeader.helpers';

interface GuestActionsProps {
  isMobile: boolean;
  onSignIn: () => void;
  currentLanguage: string;
  availableLanguages: string[];
  onLanguageChange: (lang: string) => void;
}

const LANG_BTN = cn(
  'h-9 px-(--space-4) flex items-center justify-center rounded-(--radius-md)',
  'bg-transparent border border-[var(--button-secondary-border)] cursor-pointer',
  'text-[var(--button-secondary-text)] text-sm font-(--font-regular)',
  'hover:bg-[var(--button-secondary-hover-bg)]',
  'transition-colors duration-200',
);

const SIGNIN_BTN = cn(
  'inline-flex items-center justify-center rounded-(--radius-md) h-9 px-(--space-4)',
  'bg-[var(--button-primary-bg)] border border-[var(--button-primary-bg)]',
  'text-[var(--button-primary-text)] text-sm font-(--font-regular) cursor-pointer',
  'hover:bg-[var(--button-primary-hover-bg)] hover:border-[var(--button-primary-hover-bg)]',
  'transition-colors duration-200',
);

export const GuestActions: React.FC<GuestActionsProps> = ({
  isMobile,
  onSignIn,
  currentLanguage,
  availableLanguages,
  onLanguageChange,
}) => {
  const { t } = useTranslation('@pages/landing');
  const { t: tCommon } = useTranslation('common');
  return (
    <>
      <Menu shadow="md" width={160} position="bottom-end" withArrow={false} zIndex={1001} offset={4}>
        <Menu.Target>
          <UnstyledButton
            className={cn(LANG_BTN, isMobile &&'h-8 px-(--space-3) text-xs')}
            aria-label={t('navigation.selectLanguage')}
          >
            {getLanguageLabel(currentLanguage)}
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          {availableLanguages.map((lang) => {
            const isActive = normalizeLanguage(currentLanguage) === lang;
            return (
              <Menu.Item
                key={lang}
                onClick={() => onLanguageChange(lang)}
                {...(isActive && {'data-active': true })}
              >
                {getLanguageFullName(lang, tCommon)}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>

      <UnstyledButton
        onClick={onSignIn}
        aria-label={t('navigation.signIn','Entrar')}
        className={cn(SIGNIN_BTN, isMobile &&'h-8 px-(--space-3) text-xs')}
      >
        {t('navigation.signIn','Entrar')}
      </UnstyledButton>
    </>
  );
};
